import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./SupplierLedger.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";

export default function SupplierLedger() {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({
    total_purchase_amount: 0,
    total_paid_amount: 0,
    balance_amount: 0,
  });
  const [supplierName, setSupplierName] = useState(location.state?.supplier_name || "");

  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "CASH",
    reference: "",
    date: new Date().toISOString().slice(0, 10),
  });
  const [filters, setFilters] = useState({
    search: "",
    fromDate: "",
    toDate: "",
  });

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/suppliers/${supplierId}/ledger`);
      setEntries(data?.entries || []);
      setSummary(
        data?.summary || {
          total_purchase_amount: 0,
          total_paid_amount: 0,
          balance_amount: 0,
        }
      );
    } catch (err) {
      console.error("Ledger fetch error:", err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (supplierId) {
      fetchLedger();
    }
  }, [supplierId]);

  const filteredEntries = useMemo(() => {
    const searchTerm = filters.search.trim().toLowerCase();
    const fromTime = filters.fromDate ? new Date(`${filters.fromDate}T00:00:00`).getTime() : null;
    const toTime = filters.toDate ? new Date(`${filters.toDate}T23:59:59`).getTime() : null;

    return entries.filter((entry) => {
      const entryTime = entry.date ? new Date(entry.date).getTime() : null;
      const matchesFrom = !fromTime || (entryTime && entryTime >= fromTime);
      const matchesTo = !toTime || (entryTime && entryTime <= toTime);

      if (!matchesFrom || !matchesTo) return false;
      if (!searchTerm) return true;

      const searchableText = [
        entry.type,
        entry.product_name,
        entry.quantity,
        entry.rate,
        entry.payment_method,
        entry.reference,
        entry.bank_name,
        entry.account_name,
        entry.amount,
        entry.date ? new Date(entry.date).toLocaleDateString("en-IN") : "",
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(searchTerm);
    });
  }, [entries, filters]);

  const purchaseEntries = useMemo(
    () => filteredEntries.filter((e) => e.type === "PURCHASE"),
    [filteredEntries]
  );
  const paymentEntries = useMemo(
    () => filteredEntries.filter((e) => e.type === "PAYMENT"),
    [filteredEntries]
  );

  const resetFilters = () => {
    setFilters({
      search: "",
      fromDate: "",
      toDate: "",
    });
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    const amount = Number(paymentForm.amount || 0);
    if (!amount || amount <= 0) {
      alert("Enter valid payment amount");
      return;
    }

    try {
      await axios.post(`${API_BASE}/suppliers/${supplierId}/payment`, {
        amount,
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference,
        date: paymentForm.date,
      });

      setPaymentForm({
        amount: "",
        payment_method: "CASH",
        reference: "",
        date: new Date().toISOString().slice(0, 10),
      });
      await fetchLedger();
    } catch (err) {
      console.error("Payment create error:", err);
      alert(err?.response?.data?.message || "Failed to create payment");
    }
  };

  return (
    <div className="supplier-ledger-container">
      <div className="ledger-header">
        <button type="button" className="back-btn" onClick={() => navigate("/suppliers")}>
          ← Back
        </button>
        <div className="header-content">
          <h2 className="ledger-title">Supplier Ledger {supplierName ? `- ${supplierName}` : ""}</h2>
          <p className="supplier-id">Supplier ID: {supplierId}</p>
        </div>
      </div>

      <div className="summary-cards">
        <div className="summary-card primary">
          <div className="card-content">
            <h3>Total Purchase</h3>
            <p className="card-value">₹{Number(summary.total_purchase_amount || 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="summary-card tertiary">
          <div className="card-content">
            <h3>Total Paid</h3>
            <p className="card-value">₹{Number(summary.total_paid_amount || 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="summary-card quaternary">
          <div className="card-content">
            <h3>Balance</h3>
            <p className="card-value">₹{Number(summary.balance_amount || 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="table-container" style={{ marginBottom: 16 }}>
        <h3 style={{ padding: "10px 0" }}>Add Payment</h3>
        <form className="inventory-form" onSubmit={submitPayment}>
          <input
            type="number"
            placeholder="Amount"
            value={paymentForm.amount}
            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
            required
          />
          <select
            value={paymentForm.payment_method}
            onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
          >
            <option value="CASH">CASH</option>
            <option value="UPI">UPI</option>
            <option value="BANK">BANK</option>
          </select>
          <input
            placeholder="Reference"
            value={paymentForm.reference}
            onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
          />
          <input
            type="date"
            value={paymentForm.date}
            onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
          />
          <button type="submit">Save Payment</button>
        </form>
      </div>

      <div className="table-container">
        <div className="ledger-section-header">
          <h3>Ledger Entries</h3>
          <span>
            Showing {filteredEntries.length} of {entries.length}
          </span>
        </div>
        <div className="ledger-filters">
          <div className="filter-field search-field">
            <label htmlFor="ledger-search">Search</label>
            <input
              id="ledger-search"
              type="search"
              placeholder="Search type, details, method, reference, amount"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            />
          </div>
          <div className="filter-field">
            <label htmlFor="ledger-from-date">From Date</label>
            <input
              id="ledger-from-date"
              type="date"
              value={filters.fromDate}
              max={filters.toDate || undefined}
              onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
            />
          </div>
          <div className="filter-field">
            <label htmlFor="ledger-to-date">To Date</label>
            <input
              id="ledger-to-date"
              type="date"
              value={filters.toDate}
              min={filters.fromDate || undefined}
              onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
            />
          </div>
          <button type="button" className="filter-reset-btn" onClick={resetFilters}>
            Reset
          </button>
        </div>
        <table className="ledger-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Details</th>
              <th>Method</th>
              <th>Reference</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="6" className="loading-row">
                  Loading...
                </td>
              </tr>
            )}
            {!loading &&
              filteredEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.date ? new Date(entry.date).toLocaleDateString("en-IN") : "-"}</td>
                  <td>{entry.type}</td>
                  <td>{entry.type === "PURCHASE" ? `${entry.product_name || "-"} (Qty: ${entry.quantity || 0})` : "-"}</td>
                  <td>{entry.payment_method || "-"}</td>
                  <td>{entry.reference || "-"}</td>
                  <td>₹{Number(entry.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            {!loading && filteredEntries.length === 0 && (
              <tr>
                <td colSpan="6" className="empty-row">
                  {entries.length ? "No ledger entries match the selected filters" : "No ledger entries found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="ledger-footer">
        <div className="footer-stats">
          <span>Purchases: {purchaseEntries.length}</span>
          <span>Payments: {paymentEntries.length}</span>
        </div>
      </div>
    </div>
  );
}
